// src/utils/generateExamPdf.js
// Renders a completed exam session's typed answers into a real vector PDF
// (via @react-pdf/renderer), which is then uploaded through the exact same
// createSubmission() path as a manually-uploaded PDF. This is what lets the
// entire existing marking pipeline stay completely untouched by exam mode.

import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf } from '@react-pdf/renderer';
import { buildExamGroups, calculateGroupMarks } from './examGroups';
import { evaluateGrid } from './spreadsheetFormulas';
import { colLetter, parseGrid } from '../components/ExamSpreadsheetGrid';

const styles = StyleSheet.create({
  page: { padding: 40, fontSize: 11, fontFamily: 'Helvetica' },
  header: { marginBottom: 20, borderBottom: '2 solid #c33329', paddingBottom: 10 },
  headerTitle: { fontSize: 14, fontFamily: 'Helvetica-Bold', color: '#c33329', marginBottom: 6 },
  headerLine: { fontSize: 10, color: '#444444', marginBottom: 2 },
  sectionHeading: { fontSize: 13, fontFamily: 'Helvetica-Bold', marginBottom: 10 },
  paragraph: { marginBottom: 6, lineHeight: 1.4 },
  listItem: { marginBottom: 4, lineHeight: 1.4, paddingLeft: 4 },
  heading: { marginBottom: 8, marginTop: 4, fontSize: 12, fontFamily: 'Helvetica-Bold', lineHeight: 1.4 },
  wpTable: { marginBottom: 10 },
  wpTableCell: { border: '0.5 solid #999999', padding: 4, justifyContent: 'flex-start' },
  wpTableHeaderCell: { backgroundColor: '#f0f0f0' },
  spreadsheetLabel: { marginTop: 12, marginBottom: 6, fontSize: 11, fontFamily: 'Helvetica-Bold', color: '#444444' },
  gridRow: { flexDirection: 'row' },
  // No width here - it's set per-cell as an equal share of the row (see
  // renderSpreadsheetGrid) since react-pdf's flexbox has no default shrink,
  // so an unbounded cell just grows to fit its text on one line and
  // overflows into the next cell instead of wrapping.
  gridCell: { border: '0.5 solid #999999', padding: 4 },
  gridCellText: { fontSize: 9 },
  gridFormulaText: { fontSize: 6, color: '#666666', marginTop: 1 },
  gridErrorText: { fontSize: 9, color: '#c33329' },
  spreadsheetNote: { marginTop: 12, padding: 10, border: '1 solid #1b998b', backgroundColor: '#eef8f6' },
  spreadsheetNoteTitle: { fontSize: 12, fontFamily: 'Helvetica-Bold', color: '#1b998b', marginBottom: 3 },
  spreadsheetNoteText: { fontSize: 9, color: '#444444' }
});

// Tiptap emits a constrained set of HTML tags (paragraphs, bold/italic/
// underline, bullet/numbered lists, headings) - walk it into styled
// @react-pdf/renderer nodes rather than flattening everything to plain text.

function renderInline(container, keyPrefix, inheritedStyle) {
  const nodes = [];
  container.childNodes.forEach((child, i) => {
    const key = `${keyPrefix}-${i}`;
    if (child.nodeType === 3) {
      if (child.textContent) nodes.push(<Text key={key} style={inheritedStyle}>{child.textContent}</Text>);
      return;
    }
    if (child.nodeType !== 1) return;

    const tag = child.tagName.toLowerCase();
    if (tag === 'br') {
      nodes.push(<Text key={key}>{'\n'}</Text>);
      return;
    }

    let style = inheritedStyle;
    if (tag === 'strong' || tag === 'b') {
      style = { ...style, fontFamily: style?.fontStyle === 'italic' ? 'Helvetica-BoldOblique' : 'Helvetica-Bold' };
    } else if (tag === 'em' || tag === 'i') {
      style = { ...style, fontFamily: style?.fontFamily === 'Helvetica-Bold' ? 'Helvetica-BoldOblique' : 'Helvetica-Oblique' };
    } else if (tag === 'u') {
      style = { ...style, textDecoration: 'underline' };
    } else if (tag === 's' || tag === 'strike' || tag === 'del') {
      // Combine with an existing underline rather than overwrite it -
      // textDecoration accepts a space-separated combination.
      style = { ...style, textDecoration: style?.textDecoration === 'underline' ? 'underline line-through' : 'line-through' };
    } else if (tag === 'sub') {
      const baseSize = style?.fontSize || styles.page.fontSize;
      style = { ...style, verticalAlign: 'sub', fontSize: baseSize * 0.75 };
    } else if (tag === 'sup') {
      const baseSize = style?.fontSize || styles.page.fontSize;
      style = { ...style, verticalAlign: 'super', fontSize: baseSize * 0.75 };
    }
    nodes.push(...renderInline(child, key, style));
  });
  return nodes;
}

// Tiptap's table extension always wraps rows in a <tbody> (and may add a
// <thead> for a header row) - walk both so a table survives regardless of
// which the browser/editor produced.
function getTableRows(tableNode) {
  const rows = [];
  Array.from(tableNode.children).forEach(child => {
    const tag = child.tagName.toLowerCase();
    if (tag === 'tr') rows.push(child);
    else if (tag === 'thead' || tag === 'tbody' || tag === 'tfoot') {
      Array.from(child.children).forEach(tr => { if (tr.tagName.toLowerCase() === 'tr') rows.push(tr); });
    }
  });
  return rows;
}

function renderTable(tableNode, keyPrefix) {
  const rows = getTableRows(tableNode);
  if (!rows.length) return null;
  const colCount = rows[0].children.length || 1;
  return (
    <View key={`${keyPrefix}-table`} style={styles.wpTable}>
      {rows.map((tr, ri) => (
        <View key={`${keyPrefix}-tr${ri}`} style={styles.gridRow}>
          {Array.from(tr.children).map((cell, ci) => {
            const isHeader = cell.tagName.toLowerCase() === 'th';
            return (
              <View
                key={`${keyPrefix}-tr${ri}-c${ci}`}
                style={[styles.wpTableCell, { width: `${100 / colCount}%` }, isHeader ? styles.wpTableHeaderCell : null]}
              >
                {renderBlocks(cell, `${keyPrefix}-tr${ri}-c${ci}`)}
              </View>
            );
          })}
        </View>
      ))}
    </View>
  );
}

function renderBlocks(container, keyPrefix) {
  const blocks = [];
  Array.from(container.childNodes).forEach((node, i) => {
    if (node.nodeType !== 1) return;
    const tag = node.tagName.toLowerCase();
    const key = `${keyPrefix}-b${i}`;

    if (tag === 'ul' || tag === 'ol') {
      Array.from(node.children).forEach((li, j) => {
        const bullet = tag === 'ul' ? '•  ' : `${j + 1}.  `;
        const inline = renderInline(li, `${key}-li${j}`);
        blocks.push(
          <Text key={`${key}-li${j}`} style={styles.listItem}>{bullet}{inline.length ? inline : ' '}</Text>
        );
      });
    } else if (/^h[1-6]$/.test(tag)) {
      // Pass the heading's own font size down so a <sub>/<sup> inside a
      // heading shrinks relative to 12pt, not the 11pt paragraph default.
      const inline = renderInline(node, key, { fontSize: styles.heading.fontSize });
      blocks.push(<Text key={key} style={styles.heading}>{inline.length ? inline : ' '}</Text>);
    } else if (tag === 'blockquote') {
      blocks.push(...renderBlocks(node, key));
    } else if (tag === 'table') {
      const rendered = renderTable(node, key);
      if (rendered) blocks.push(rendered);
    } else {
      // Paragraphs and anything else block-level: render as a paragraph
      const inline = renderInline(node, key);
      blocks.push(<Text key={key} style={styles.paragraph}>{inline.length ? inline : ' '}</Text>);
    }
  });
  if (blocks.length === 0) {
    blocks.push(<Text key={`${keyPrefix}-empty`} style={styles.paragraph}> </Text>);
  }
  return blocks;
}

// `label` is only passed (and only then printed) when a "Blank Word
// Processor" counterpart also exists for this question, so the ordinary
// single-word-processor case renders identically to before this ever
// existed - see the ExamPdfDocument call site below.
function renderAnswerHtml(html, keyPrefix, label) {
  const parsed = new DOMParser().parseFromString(html || '', 'text/html');
  const blocks = renderBlocks(parsed.body, keyPrefix);
  if (!label) return blocks;
  return (
    <View key={`${keyPrefix}-labeled`}>
      <Text style={styles.spreadsheetLabel}>{label}</Text>
      {blocks}
    </View>
  );
}

// A cell is either the current {value, bold, italic, underline, align}
// shape, or a legacy plain string from before formatting existed
function cellValue(cell) {
  return (cell && typeof cell === 'object' ? cell.value : cell) || '';
}

// A formula's computed result, as printed in the PDF: at most 2 decimal
// places (157843.137254902 -> 157843.14). The PDF can only wrap text at
// spaces, so an unbroken full-precision result used to spill across the
// neighbouring cells (reported 2026-09-28). The formula itself still prints
// underneath, so the marker sees exactly how it was worked out. Typed
// (non-formula) values are always shown exactly as the student typed them.
export function formatFormulaResultForPdf(numeric) {
  if (typeof numeric !== 'number' || !isFinite(numeric)) return null;
  if (Number.isInteger(numeric)) return String(numeric);
  return String(Math.round(numeric * 100) / 100);
}

// The PDF can only wrap text at spaces, so any unbroken run longer than the
// cell (a long formula, or a long number in a narrow column) is split with
// real line breaks, preferring to break just after an operator. Estimated
// from the cell's width (less its 4pt padding and border) and Helvetica's
// digit width (0.556em) - conservatively, so text never spills into the
// neighbouring cell.
export function charsThatFit(cellWidthPt, fontSize) {
  return Math.max(4, Math.floor((cellWidthPt - 9) / (fontSize * 0.6)));
}

// Space for a grid inside the page's 40pt margins
export const PORTRAIT_GRID_WIDTH = 595.28 - 80;
export const LANDSCAPE_GRID_WIDTH = 841.89 - 80;

export function breakToFit(text, maxChars) {
  return String(text).split(' ').map(word => {
    if (word.length <= maxChars) return word;
    const lines = [];
    let rest = word;
    while (rest.length > maxChars) {
      const window = rest.slice(0, maxChars);
      // Break after the last operator/separator in the window, if there is one
      let cut = -1;
      for (let i = window.length - 1; i > maxChars / 3; i--) {
        if ('+-*/^,:()'.includes(window[i])) { cut = i + 1; break; }
      }
      if (cut <= 0) cut = maxChars;
      lines.push(rest.slice(0, cut));
      rest = rest.slice(cut);
    }
    lines.push(rest);
    return lines.join('\n');
  }).join(' ');
}

// cellEval (optional) lets a cell with no alignment of its own use Excel's
// "General" alignment - numbers right, text left - same as on screen.
function cellPdfStyle(cell, cellEval) {
  if (!cell || typeof cell !== 'object') return styles.gridCellText;
  const style = { ...styles.gridCellText };
  if (cell.bold && cell.italic) style.fontFamily = 'Helvetica-BoldOblique';
  else if (cell.bold) style.fontFamily = 'Helvetica-Bold';
  else if (cell.italic) style.fontFamily = 'Helvetica-Oblique';
  if (cell.underline) style.textDecoration = 'underline';
  if (cell.align) style.textAlign = cell.align;
  else if (cellEval && !cellEval.isError && cellEval.numeric !== null && cellEval.numeric !== undefined) style.textAlign = 'right';
  // Scaled down from the on-screen size (13px default) to the grid's
  // smaller base PDF size (9pt default) so relative size differences carry
  // over without blowing up the compact table layout.
  if (cell.fontSize) style.fontSize = Math.round(cell.fontSize * (9 / 13));
  if (cell.fontColor) style.color = cell.fontColor;
  return style;
}

function cellFillStyle(cell) {
  return (cell && typeof cell === 'object' && cell.fillColor) ? { backgroundColor: cell.fillColor } : {};
}

// The student's own cell borders (see ExamSpreadsheetGrid's cellBorderCss),
// drawn in black over the grey gridline on that side. react-pdf has no
// double border style, so a double line is a thin border plus a second
// thin line just inside it (see cellDoubleLines).
const PDF_BORDER_WIDTH = { thin: 1, thick: 2, double: 0.75 };
function cellBorderStyle(cell) {
  const borders = cell && typeof cell === 'object' ? cell.borders : null;
  if (!borders || typeof borders !== 'object') return {};
  const style = {};
  ['top', 'bottom', 'left', 'right'].forEach(side => {
    const width = PDF_BORDER_WIDTH[borders[side]];
    if (!width) return;
    const key = `border${side[0].toUpperCase()}${side.slice(1)}`;
    style[`${key}Width`] = width;
    style[`${key}Color`] = '#000000';
    style[`${key}Style`] = 'solid';
  });
  return style;
}

function cellDoubleLines(cell, keyPrefix) {
  const borders = cell && typeof cell === 'object' ? cell.borders : null;
  if (!borders || typeof borders !== 'object') return null;
  const lines = [];
  if (borders.top === 'double') lines.push({ side: 'top', box: { top: 1.5, left: 0, right: 0, height: 0 } });
  if (borders.bottom === 'double') lines.push({ side: 'bottom', box: { bottom: 1.5, left: 0, right: 0, height: 0 } });
  if (borders.left === 'double') lines.push({ side: 'left', box: { left: 1.5, top: 0, bottom: 0, width: 0 } });
  if (borders.right === 'double') lines.push({ side: 'right', box: { right: 1.5, top: 0, bottom: 0, width: 0 } });
  if (!lines.length) return null;
  return lines.map(({ side, box }) => {
    const key = `border${side[0].toUpperCase()}${side.slice(1)}`;
    return (
      <View
        key={`${keyPrefix}-double-${side}`}
        style={{ position: 'absolute', ...box, [`${key}Width`]: 0.75, [`${key}Color`]: '#000000', [`${key}Style`]: 'solid' }}
      />
    );
  });
}

// Renders a spreadsheet answer (a JSON-stringified 2D array of cells, or the
// current { grid, colWidths } shape ExamSpreadsheetGrid saves - see its
// parseGrid) as a bordered table, preserving per-cell bold/italic/underline/
// alignment. Returns null if the grid has no content at all, so a
// word-processor-only answer doesn't get a blank table.
//
// precomputedEval (optional) - a same-shaped 2D array of evaluateGrid()'s own
// {display, isError, numeric, isBlank, isFormula} output - lets a caller
// supply cell values from elsewhere (see parseUploadedSpreadsheet.js, which
// uses Excel's own cached values instead of re-evaluating formulas with this
// codebase's limited formula engine) instead of running evaluateGrid here.
// Whether a saved grid has anything typed in it (renderSpreadsheetGrid
// leaves an empty one out of the PDF altogether)
export function gridHasContent(gridJson) {
  if (!gridJson) return false;
  try {
    const parsed = JSON.parse(gridJson);
    const grid = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.grid) ? parsed.grid : null);
    return !!grid && grid.some(row => row.some(cell => cellValue(cell).trim() !== ''));
  } catch (e) {
    return false;
  }
}

// "Spreadsheet 1", "Spreadsheet 2"... through the exam in question order,
// counting only grids with something in them: { [groupId]: { spreadsheet,
// blankSpreadsheet } }. Used by the "See Spreadsheet N" notes below and
// saved with the submission's spreadsheetAnswers (ExamWindowApp.jsx) so
// marking and the marked PDF use the same numbers.
export function numberSpreadsheets(groups, answers) {
  const numbers = {};
  let n = 0;
  groups.forEach(group => {
    ['spreadsheet', 'blankSpreadsheet'].forEach(key => {
      if (gridHasContent(answers?.[group.id]?.[key]?.gridJson)) {
        numbers[group.id] = { ...numbers[group.id], [key]: ++n };
      }
    });
  });
  return numbers;
}

// Stands in for a grid when the tutor marks spreadsheets as spreadsheets
// only (Settings > Interactive Spreadsheet Marking > "Spreadsheet only").
// Records the page it lands on in pageOf, so the marked PDF can put the
// marked spreadsheet straight after it.
export function renderSpreadsheetNote(number, whose, keyPrefix, pageOf) {
  return (
    <View key={`${keyPrefix}-note`} wrap={false} style={styles.spreadsheetNote}>
      <Text
        style={styles.spreadsheetNoteTitle}
        render={({ pageNumber }) => {
          if (pageOf) pageOf[keyPrefix] = pageNumber;
          return `See Spreadsheet ${number}`;
        }}
      />
      <Text style={styles.spreadsheetNoteText}>
        {`${whose} is marked as a spreadsheet. In your marked script it comes straight after this page.`}
      </Text>
    </View>
  );
}

// The grid with fully-empty trailing rows/columns trimmed off (so the PDF
// doesn't render dozens of blank cells from the default-sized grid), its
// values, and the student's own column widths in points. Null if the grid
// has nothing in it.
function prepareGrid(gridJson, precomputedEval) {
  if (!gridJson) return null;

  let grid;
  try {
    const parsed = JSON.parse(gridJson);
    grid = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.grid) ? parsed.grid : null);
  } catch (e) {
    return null;
  }
  if (!grid || grid.length === 0) return null;

  const hasContent = grid.some(row => row.some(cell => cellValue(cell).trim() !== ''));
  if (!hasContent) return null;

  let lastRow = grid.length - 1;
  while (lastRow > 0) {
    let rowHasContent = false;
    for (let c = 0; c < grid[lastRow].length; c++) {
      if (cellValue(grid[lastRow][c])) { rowHasContent = true; break; }
    }
    if (rowHasContent) break;
    lastRow--;
  }

  const colCount = grid[0]?.length || 0;
  let lastCol = colCount - 1;
  while (lastCol > 0) {
    let colHasContent = false;
    for (let r = 0; r < grid.length; r++) {
      if (cellValue(grid[r][lastCol])) { colHasContent = true; break; }
    }
    if (colHasContent) break;
    lastCol--;
  }

  // Evaluated against the full grid (so a formula referencing a
  // since-trimmed-away blank cell still resolves correctly), then trimmed
  // the same way as the grid itself for display. precomputedEval (when
  // given) skips this engine entirely - see renderSpreadsheetGrid's comment.
  const evaluated = precomputedEval || evaluateGrid(grid);
  const { colWidths } = parseGrid(gridJson);
  return {
    trimmed: grid.slice(0, lastRow + 1).map(row => row.slice(0, lastCol + 1)),
    trimmedEval: evaluated.slice(0, lastRow + 1).map(row => row.slice(0, lastCol + 1)),
    widths: Array.from({ length: lastCol + 1 }, (_, c) => (colWidths[c] || 90) * 0.75)
  };
}

// How a grid fits the page, from the student's own column widths: on a
// portrait page if it fits at 75% of its size or more, otherwise on a
// landscape page if it fits at 90% or more, otherwise split into bands of
// columns that each do, with column A repeated so the rows can still be
// read. (The marked spreadsheet pages - markedSpreadsheetPages.js - allow
// landscape down to 60%, as their text shrinks with the grid; here it
// stays 9pt, so narrower columns would wrap numbers.) Null if the grid has
// nothing in it.
export function spreadsheetGridLayout(gridJson, precomputedEval = null) {
  const prepared = prepareGrid(gridJson, precomputedEval);
  if (!prepared) return null;
  const { widths } = prepared;
  const width = (cols) => cols.reduce((sum, c) => sum + widths[c], 0);
  const all = widths.map((_, c) => c);
  if (width(all) * 0.75 <= PORTRAIT_GRID_WIDTH) return { landscape: false, bands: [all] };
  if (width(all) * 0.9 <= LANDSCAPE_GRID_WIDTH) return { landscape: true, bands: [all] };
  const room = LANDSCAPE_GRID_WIDTH / 0.9;
  const bands = [];
  let band = [];
  all.forEach(c => {
    if (band.length > (bands.length ? 1 : 0) && width([...band, c]) > room) {
      bands.push(band);
      band = [0];
    }
    band.push(c);
  });
  bands.push(band);
  return { landscape: true, bands };
}

export function columnsLabel(cols) {
  const parts = [];
  let i = 0;
  while (i < cols.length) {
    let j = i;
    while (j + 1 < cols.length && cols[j + 1] === cols[j] + 1) j++;
    parts.push(j > i ? `${colLetter(cols[i])} to ${colLetter(cols[j])}` : colLetter(cols[i]));
    i = j + 1;
  }
  return parts.join(', ');
}

// `options.cols` draws only those columns (one band of a wide grid - see
// spreadsheetGridLayout), and `options.gridWidth` is the width available,
// shared between the columns in proportion to the student's own widths.
export function renderSpreadsheetGrid(gridJson, keyPrefix, label = 'Spreadsheet', precomputedEval = null, options = {}) {
  const prepared = prepareGrid(gridJson, precomputedEval);
  if (!prepared) return null;
  const { trimmed, trimmedEval, widths } = prepared;
  const cols = options.cols || widths.map((_, c) => c);
  const gridWidth = options.gridWidth || PORTRAIT_GRID_WIDTH;
  const totalWidth = cols.reduce((sum, c) => sum + widths[c], 0) || 1;
  // Filled out to a portrait page's width at least (as grids always were),
  // but a narrow grid isn't stretched right across a landscape page
  const drawnWidth = Math.min(gridWidth, Math.max(totalWidth, PORTRAIT_GRID_WIDTH));
  const cellWidth = (c) => drawnWidth * widths[c] / totalWidth;

  return (
    <View key={`${keyPrefix}-grid`}>
      <Text style={styles.spreadsheetLabel}>{label}</Text>
      <View>
        {trimmed.map((row, ri) => (
          <View key={`${keyPrefix}-grid-row-${ri}`} style={styles.gridRow} wrap={false}>
            {cols.map(ci => {
              const cell = row[ci];
              const cellEval = trimmedEval[ri]?.[ci];
              // Shows the computed result (what the answer actually is), plus
              // the formula itself in small print underneath when there is
              // one - ACCA's marking guidance explicitly says formula use
              // isn't penalised because the marker can see what formula was
              // used, so this is what makes that true for a flattened PDF.
              const isFormula = cellEval?.isFormula;
              const rawValue = cellValue(cell);
              // A number format (see spreadsheetFormulas' formatWithNumFormat)
              // is already applied in cellEval.display - use it as-is rather
              // than the default 2-decimal rounding below.
              const hasNumFormat = !!(cell && typeof cell === 'object' && cell.numFormat);
              const textStyle = cellEval?.isError ? styles.gridErrorText : cellPdfStyle(cell, cellEval);
              return (
                <View
                  key={`${keyPrefix}-grid-cell-${ri}-${ci}`}
                  style={[styles.gridCell, { width: cellWidth(ci) }, cellFillStyle(cell), cellBorderStyle(cell)]}
                >
                  {cellDoubleLines(cell, `${keyPrefix}-grid-cell-${ri}-${ci}`)}
                  <Text style={textStyle}>
                    {breakToFit(
                      (isFormula && !cellEval.isError && !hasNumFormat && formatFormulaResultForPdf(cellEval.numeric)) ||
                        (cellEval ? cellEval.display : rawValue) || ' ',
                      charsThatFit(cellWidth(ci), textStyle.fontSize || 9)
                    )}
                  </Text>
                  {isFormula && (
                    // Led by the cell's own reference (D6: =SUM(D3:D5)) so the
                    // formula can be followed - the grid has no row/column labels.
                    // Only trailing rows/columns are trimmed, so ri/ci are the real ones.
                    <Text style={styles.gridFormulaText}>{breakToFit(`${colLetter(ci)}${ri + 1}: ${rawValue}`, charsThatFit(cellWidth(ci), styles.gridFormulaText.fontSize))}</Text>
                  )}
                </View>
              );
            })}
          </View>
        ))}
      </View>
    </View>
  );
}

function formatDuration(ms) {
  if (!ms || ms < 0) return 'N/A';
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

function ExamPdfDocument({ session, homeworkName }) {
  const {
    studentName,
    cohortName,
    homeworkType,
    sectionStructureSnapshot,
    answers,
    startedAtMs,
    submittedAtMs,
    // Set when spreadsheets are marked as spreadsheets only: each grid is
    // replaced by a "See Spreadsheet N" note, and pageOf collects the page
    // each note is on, keyed `${groupId}:${spreadsheet|blankSpreadsheet}`
    spreadsheetNotes,
    pageOf
  } = session;

  const timeTaken = formatDuration(submittedAtMs - startedAtMs);
  const groups = buildExamGroups(sectionStructureSnapshot);
  const sheetNumbers = spreadsheetNotes ? numberSpreadsheets(groups, answers) : {};

  return (
    <Document>
      {groups.map((group, idx) => {
        const totalMarks = calculateGroupMarks(group, sectionStructureSnapshot);
        // A second grid only exists once the head marker turns on
        // "Include blank spreadsheet as well" for this question - see
        // functions/index.js's initialAnswers seeding. Its mere
        // presence (not whether the student put anything in it) is
        // what decides the "Prepopulated"/"Blank" labels below, same
        // rule the exam UI itself uses for its two Responses buttons.
        const hasBlankSpreadsheet = answers?.[group.id]?.blankSpreadsheet !== undefined;
        const sheets = spreadsheetNotes ? [] : [
          { gridJson: answers?.[group.id]?.spreadsheet?.gridJson, keyPrefix: group.id, label: hasBlankSpreadsheet ? 'Prepopulated Spreadsheet' : 'Spreadsheet' },
          hasBlankSpreadsheet && { gridJson: answers?.[group.id]?.blankSpreadsheet?.gridJson, keyPrefix: `${group.id}-blank`, label: 'Blank Spreadsheet' }
        ].filter(Boolean)
          .map(sheet => ({ ...sheet, layout: spreadsheetGridLayout(sheet.gridJson) }))
          .filter(sheet => sheet.layout);
        // Too wide for a portrait page: the question's spreadsheets go on
        // landscape pages straight after its written answer, in order
        const landscape = sheets.some(sheet => sheet.layout.landscape);
        const renderSheets = (gridWidth) => sheets.map(sheet => sheet.layout.bands.map((cols, bandIndex) => renderSpreadsheetGrid(
          sheet.gridJson,
          `${sheet.keyPrefix}-band${bandIndex}`,
          sheet.layout.bands.length > 1 ? `${sheet.label}: columns ${columnsLabel(cols)}` : sheet.label,
          null,
          { cols, gridWidth }
        )));
        return (
          <React.Fragment key={group.id}>
            <Page size="A4" style={styles.page}>
              {idx === 0 && (
                <View style={styles.header}>
                  <Text style={styles.headerTitle}>EXAM SIMULATION SUBMISSION</Text>
                  <Text style={styles.headerLine}>Student: {studentName}</Text>
                  <Text style={styles.headerLine}>
                    Homework: {homeworkName || homeworkType}
                  </Text>
                  <Text style={styles.headerLine}>Cohort: {cohortName}</Text>
                  <Text style={styles.headerLine}>Time taken: {timeTaken}</Text>
                </View>
              )}
              <Text style={styles.sectionHeading}>
                {group.name}
                {totalMarks ? ` (${totalMarks} marks)` : ''}
              </Text>
              {(() => {
                const hasBlankWordProcessor = answers?.[group.id]?.blankWordProcessor !== undefined;
                return (
                  <>
                    {renderAnswerHtml(answers?.[group.id]?.wordProcessor?.html, group.id, hasBlankWordProcessor ? 'Prepopulated Word Processor' : null)}
                    {hasBlankWordProcessor && renderAnswerHtml(answers?.[group.id]?.blankWordProcessor?.html, `${group.id}-blank-wp`, 'Blank Word Processor')}
                  </>
                );
              })()}
              {spreadsheetNotes && (() => {
                const numbers = sheetNumbers[group.id] || {};
                return (
                  <>
                    {numbers.spreadsheet && renderSpreadsheetNote(numbers.spreadsheet, hasBlankSpreadsheet ? 'Your prepopulated spreadsheet' : 'Your spreadsheet', `${group.id}:spreadsheet`, pageOf)}
                    {numbers.blankSpreadsheet && renderSpreadsheetNote(numbers.blankSpreadsheet, 'Your blank spreadsheet', `${group.id}:blankSpreadsheet`, pageOf)}
                  </>
                );
              })()}
              {!landscape && renderSheets(PORTRAIT_GRID_WIDTH)}
              {landscape && (
                <Text style={styles.spreadsheetLabel}>
                  {`${sheets.map(sheet => sheet.label).join(' and ')}: on the next page, turned sideways to fit.`}
                </Text>
              )}
            </Page>
            {landscape && (
              <Page size="A4" orientation="landscape" style={styles.page}>
                <Text style={styles.sectionHeading}>{group.name} (continued)</Text>
                {renderSheets(LANDSCAPE_GRID_WIDTH)}
              </Page>
            )}
          </React.Fragment>
        );
      })}
    </Document>
  );
}

/**
 * Build the exam session's PDF and wrap it as a real File (not a bare Blob) -
 * createSubmission() reads file.name and passes the file straight into
 * uploadBytesResumable, so this is the only integration point it needs.
 */
export async function generateExamPdf(session, homeworkName) {
  const blob = await pdf(
    <ExamPdfDocument session={session} homeworkName={homeworkName} />
  ).toBlob();

  return new File([blob], `${session.homeworkType}_exam.pdf`, { type: 'application/pdf' });
}
